/**
 * M32109 — Appendix 문서별 BO 등록 슬롯 수집.
 * 다건 등록 / CONTACT_CD 중복 스킵 / 수동입력 Contact 선생성에 사용.
 */
import type { BoAppendixDocCode } from '../components/boAppendix/boAppendixFields';
import { parseMalaysianAddress, isEmptyMyAddress } from '@/shared/utils/malaysianAddress';

export type BoRegisterAddr = {
  street01: string;
  street02: string;
  street03: string;
  city: string;
  state: string;
  post_code: string;
  country: string;
};

export interface BoAppendixRegisterSlot {
  docCode: BoAppendixDocCode;
  /** values 키 — contact 생성 후 주입 대상 */
  contactCdKey: string;
  contactCd: string;
  name: string;
  isCompany: boolean;
  isSeniorManagement: boolean;
  smReasonId: number;
  becomingDate: string;
  idNo: string;
  nationality: string;
  dob: string;
  gender: string;
  race: string;
  email: string;
  phone: string;
  occupation: string;
  residentialAddress: BoRegisterAddr | null;
  businessAddress: BoRegisterAddr | null;
  criteria: Record<string, number>;
  percents: Record<string, string>;
  ownPercent: number | null;
  isJointArrangement: boolean;
}

const DOC_LABELS: Record<string, string> = {
  BA01: 'Appendix A',
  BA02: 'Appendix B (Corporate)',
  BA03: 'Appendix B (Individual)',
  BA04: 'Appendix C (Corporate)',
  BA05: 'Appendix C (Individual)',
};

type DocPersonKind = 'bo' | 'sm';

const DOC_SPECS: Array<{
  docCode: BoAppendixDocCode;
  prefix: string;
  kind: DocPersonKind;
  /** BO 주체(법인/개인). SM(Appendix C) 인적은 항상 개인 → Contact/주소 경로에 쓰지 않음 */
  isCompany: boolean;
  isSeniorManagement: boolean;
}> = [
  { docCode: 'BA01', prefix: '', kind: 'bo', isCompany: false, isSeniorManagement: false },
  { docCode: 'BA02', prefix: 'ba02_', kind: 'bo', isCompany: true, isSeniorManagement: false },
  { docCode: 'BA03', prefix: 'ba03_', kind: 'bo', isCompany: false, isSeniorManagement: false },
  // Appendix C — SM 인적(표)은 개인. 법인 member 는 별도 필드. Contact·주소는 개인(B09002) 경로.
  { docCode: 'BA04', prefix: 'ba04_', kind: 'sm', isCompany: false, isSeniorManagement: true },
  { docCode: 'BA05', prefix: 'ba05_', kind: 'sm', isCompany: false, isSeniorManagement: true },
];

function extractValue(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    return String(o.value ?? o.label ?? o.code ?? o.cd ?? '').trim();
  }
  return String(raw).trim();
}

function parseFlag(v: unknown): boolean {
  const s = String(v ?? '').trim();
  return v === true || v === 1 || s === '☒' || s === '1'
    || s.toLowerCase() === 'true' || s.toLowerCase() === 'yes';
}

function toNumFlag(v: unknown): number {
  return parseFlag(v) ? 1 : 0;
}

function toPctNumber(v: string): number | null {
  const s = String(v || '').replace(/%/g, '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function pickPct(...candidates: unknown[]): string {
  for (const c of candidates) {
    const s = extractValue(c);
    if (s !== '') return s;
  }
  return '';
}

function emptyBoAddr(): BoRegisterAddr {
  return { street01: '', street02: '', street03: '', city: '', state: '', post_code: '', country: '' };
}

/** 한 줄 주소 → BO/Contact 저장용 구조화 (town/state/postcode/country 분리) */
function addrFromComposedLine(line: string): BoRegisterAddr | null {
  const raw = String(line || '').trim();
  if (!raw) return null;
  const parsed = parseMalaysianAddress(raw);
  if (!isEmptyMyAddress(parsed)) {
    return {
      street01: String(parsed.street || raw).trim(),
      street02: '',
      street03: '',
      city: String(parsed.town || '').trim(),
      state: String(parsed.state || '').trim(),
      post_code: String(parsed.postcode || '').trim(),
      country: String(parsed.country || '').trim(),
    };
  }
  return { ...emptyBoAddr(), street01: raw };
}

/** street01만 있고 세부 칸이 비면 한 줄 파싱으로 보강 */
function enrichSparseAddr(addr: BoRegisterAddr): BoRegisterAddr {
  if (addr.city || addr.state || addr.post_code || addr.country) return addr;
  if (!addr.street01) return addr;
  const enriched = addrFromComposedLine(
    [addr.street01, addr.street02, addr.street03].filter(Boolean).join(', '),
  );
  return enriched || addr;
}

function normalizeAddrObj(structured: unknown): BoRegisterAddr | null {
  if (!structured || typeof structured !== 'object') return null;
  const s = structured as Record<string, unknown>;
  const street01 = String(s.street01 || s.street || s.line || s.Address || s.addressLine1 || '').trim();
  const street02 = String(s.street02 || s.addressLine2 || '').trim();
  const street03 = String(s.street03 || s.addressLine3 || '').trim();
  const city = String(s.city || s.town || s.Town || '').trim();
  const state = String(s.state || s.State || '').trim();
  const post_code = String(s.post_code || s.postcode || s.PostCode || s.postalCode || '').trim();
  const country = String(s.country || s.Country || '').trim();
  if (!(street01 || street02 || city || state || post_code || country)) return null;
  return enrichSparseAddr({ street01, street02, street03, city, state, post_code, country });
}

function pickAddr(
  addressesMap: Record<string, unknown>,
  vals: Record<string, unknown>,
  keys: string[],
): BoRegisterAddr | null {
  for (const k of keys) {
    const fromMap = normalizeAddrObj(addressesMap?.[k]);
    if (fromMap) return fromMap;
    const line = extractValue(vals[k]);
    if (line) return addrFromComposedLine(line);
  }
  return null;
}

function parseSmReasonId(vals: Record<string, unknown>, prefix: string): number {
  const reasonFromLabel = (text: unknown): number => {
    const t = String(text ?? '').toLowerCase();
    if (!t.trim()) return 0;
    if (/no beneficial owner/.test(t)) return 1;
    if (/cannot be identified/.test(t)) return 2;
    if (/obtaining ownership|in the process/.test(t)) return 3;
    return 0;
  };
  const candidates = [
    vals[`${prefix}sm_reason_id`],
    vals.sm_reason_id,
    reasonFromLabel(vals[`${prefix}sm_reason`]),
    reasonFromLabel(vals.sm_reason),
  ];
  for (const raw of candidates) {
    const n = Number(String(raw ?? '').trim());
    if (n === 1 || n === 2 || n === 3) return n;
  }
  return 0;
}

function buildCriteria(
  criteriaByDoc: Record<string, any>,
  docCode: string,
  vals: Record<string, unknown>,
  prefix: string,
): Record<string, number> {
  const c = criteriaByDoc?.[docCode] || {};
  const cat = c?.cat || {};
  const critDoc = c?.crit || {};
  return {
    cat_direct: toNumFlag(cat.direct ?? vals[`${prefix}cat_direct`] ?? vals.cat_direct),
    cat_indirect: toNumFlag(cat.indirect ?? vals[`${prefix}cat_indirect`] ?? vals.cat_indirect),
    cat_both: toNumFlag(cat.both ?? vals[`${prefix}cat_both`] ?? vals.cat_both),
    cat_control: toNumFlag(cat.control ?? vals[`${prefix}cat_control`] ?? vals.cat_control),
    crit_direct_shares: toNumFlag(critDoc.da ?? vals[`${prefix}crit_direct_shares`] ?? vals.crit_direct_shares),
    crit_direct_voting: toNumFlag(critDoc.db ?? vals[`${prefix}crit_direct_voting`] ?? vals.crit_direct_voting),
    crit_indirect_shares: toNumFlag(critDoc.ia ?? vals[`${prefix}crit_indirect_shares`] ?? vals.crit_indirect_shares),
    crit_indirect_voting: toNumFlag(critDoc.ib ?? vals[`${prefix}crit_indirect_voting`] ?? vals.crit_indirect_voting),
    crit_combined_shares: toNumFlag(critDoc.ba ?? vals[`${prefix}crit_combined_shares`] ?? vals.crit_combined_shares),
    crit_combined_voting: toNumFlag(critDoc.bb ?? vals[`${prefix}crit_combined_voting`] ?? vals.crit_combined_voting),
    crit_c: toNumFlag(critDoc.c ?? vals[`${prefix}crit_c`] ?? vals.crit_c ?? vals.crit_direct_appoint_majority),
    crit_d: toNumFlag(critDoc.d ?? vals[`${prefix}crit_d`] ?? vals.crit_d ?? vals.crit_direct_director),
    crit_e: toNumFlag(critDoc.e ?? vals[`${prefix}crit_e`] ?? vals.crit_e ?? vals.crit_direct_member),
    crit_f: toNumFlag(critDoc.f ?? vals[`${prefix}crit_f`] ?? vals.crit_f ?? vals.crit_control_f),
    crit_direct_appoint_majority: toNumFlag(critDoc.c ?? vals.crit_direct_appoint_majority),
    crit_direct_director: toNumFlag(critDoc.d ?? vals.crit_direct_director),
    crit_direct_member: toNumFlag(critDoc.e ?? vals.crit_direct_member),
  };
}

function buildPercents(
  criteriaByDoc: Record<string, any>,
  docCode: string,
  vals: Record<string, unknown>,
  prefix: string,
): Record<string, string> {
  const pctDoc = criteriaByDoc?.[docCode]?.pct || {};
  const pick = (base: string, docVal: unknown) => pickPct(
    docVal,
    vals[`${prefix}${base}`],
    vals[base],
  );
  return {
    pct_direct_shares: pick('pct_direct_shares', pctDoc.direct_shares),
    pct_direct_voting: pick('pct_direct_voting', pctDoc.direct_voting),
    pct_indirect_shares: pick('pct_indirect_shares', pctDoc.indirect_shares),
    pct_indirect_voting: pick('pct_indirect_voting', pctDoc.indirect_voting),
    pct_combined_shares: pick('pct_combined_shares', pctDoc.combined_shares),
    pct_combined_voting: pick('pct_combined_voting', pctDoc.combined_voting),
  };
}

function resolveOwnPercent(percents: Record<string, string>): number | null {
  const ownDirect = toPctNumber(percents.pct_direct_shares);
  const ownIndirect = toPctNumber(percents.pct_indirect_shares);
  const ownCombined = toPctNumber(percents.pct_combined_shares);
  let ownPercentResolved: number | null = ownCombined;
  if (ownPercentResolved == null && ownDirect != null && ownIndirect != null) {
    ownPercentResolved = Math.round((ownDirect + ownIndirect) * 100) / 100;
  }
  if (ownPercentResolved == null) {
    ownPercentResolved = ownDirect ?? ownIndirect;
  }
  return ownPercentResolved;
}

export function docLabelForBoAppendix(docCode: string): string {
  return DOC_LABELS[String(docCode || '').toUpperCase()] || docCode;
}

/**
 * includedDocs 기준 문서별 BO/SM 등록 슬롯.
 * 이름이 비어 있으면 스킵. contactCd 없어도 수동입력 슬롯으로 포함.
 */
export function collectBoAppendixRegisterSlots(presetForm: any): BoAppendixRegisterSlot[] {
  const vals: Record<string, unknown> =
    (presetForm?.state?.values && typeof presetForm.state.values === 'object')
      ? presetForm.state.values
      : ((presetForm?.values && typeof presetForm.values === 'object') ? presetForm.values : {});
  const addressesMap: Record<string, unknown> =
    (presetForm?.state?.addresses && typeof presetForm.state.addresses === 'object')
      ? presetForm.state.addresses
      : ((presetForm?.addresses && typeof presetForm.addresses === 'object') ? presetForm.addresses : {});
  const criteriaByDoc: Record<string, any> =
    presetForm?.state?.criteriaByDoc || presetForm?.criteriaByDoc || {};
  const includedRaw = presetForm?.includedDocs;
  const included: string[] = Array.isArray(includedRaw)
    ? includedRaw.map((d) => String(d || '').toUpperCase()).filter(Boolean)
    : [];
  const includeSet = new Set(included);
  const specs = DOC_SPECS.filter((s) => includeSet.size === 0 || includeSet.has(s.docCode));

  const slots: BoAppendixRegisterSlot[] = [];
  for (const spec of specs) {
    const p = spec.prefix;
    const isSm = spec.kind === 'sm';
    const name = extractValue(
      isSm
        ? (vals[`${p}sm_full_name`] ?? vals.sm_full_name)
        : (vals[`${p}bo_full_name`] ?? vals.bo_full_name),
    );
    if (!name) continue;

    const contactCdKey = isSm ? `${p}sm_contact_cd` : `${p}bo_contact_cd`;
    const contactCd = extractValue(vals[contactCdKey] || vals[contactCdKey.replace(/^ba0\d_/, '')]);
    const becomingDate = extractValue(
      isSm
        ? (vals[`${p}sm_appointment_date`] ?? vals.sm_appointment_date)
        : (vals[`${p}bo_date_became`] ?? vals.bo_date_became),
    );
    const idNo = extractValue(
      isSm
        ? (vals[`${p}sm_id_no`] ?? vals.sm_id_no)
        : (vals[`${p}bo_id_no`] ?? vals.bo_id_no),
    );
    const nationality = extractValue(
      isSm
        ? (vals[`${p}sm_nationality`] ?? vals.sm_nationality)
        : (vals[`${p}bo_nationality`] ?? vals.bo_nationality),
    );
    const dob = extractValue(
      isSm
        ? (vals[`${p}sm_dob`] ?? vals.sm_dob)
        : (vals[`${p}bo_dob`] ?? vals.bo_dob),
    );
    const gender = extractValue(
      isSm
        ? (vals[`${p}sm_gender`] ?? vals.sm_gender)
        : (vals[`${p}bo_gender`] ?? vals.bo_gender),
    );
    const race = extractValue(
      isSm
        ? (vals[`${p}sm_race`] ?? vals.sm_race)
        : (vals[`${p}bo_race`] ?? vals.bo_race),
    );
    const email = extractValue(
      isSm
        ? (vals[`${p}sm_email`] ?? vals.sm_email)
        : (vals[`${p}bo_email`] ?? vals.bo_email),
    );
    const phone = extractValue(
      isSm
        ? (vals[`${p}sm_phone`] ?? vals.sm_phone)
        : (vals[`${p}bo_contact_no`] ?? vals.bo_contact_no ?? vals[`${p}bo_phone`]),
    );
    const occupation = extractValue(
      isSm
        ? (vals[`${p}sm_position`] ?? vals.sm_position)
        : (vals[`${p}bo_position`] ?? vals.bo_position),
    );

    // Appendix B: residential / business 독립
    // Appendix C: sm_residential → residential, sm_office → business
    // prefix 문서(BA02~05)는 타 문서(BA01 등) unprefixed 키로 fallback 하지 않음
    const residentialAddress = isSm
      ? pickAddr(
        addressesMap,
        vals,
        p
          ? [`${p}sm_residential_address`]
          : ['sm_residential_address'],
      )
      : pickAddr(
        addressesMap,
        vals,
        p
          ? [`${p}bo_residential_address`]
          : ['bo_residential_address'],
      );
    const businessAddress = isSm
      ? pickAddr(
        addressesMap,
        vals,
        p
          ? [`${p}sm_office_address`]
          : ['sm_office_address'],
      )
      : pickAddr(
        addressesMap,
        vals,
        p
          ? [`${p}bo_business_address`]
          : ['bo_business_address'],
      );

    const percents = buildPercents(criteriaByDoc, spec.docCode, vals, p);
    // SM(Appendix C) 인적은 항상 개인 Contact. BO 법인(BA02)만 isCompany.
    const isCompany = isSm
      ? false
      : (spec.isCompany || parseFlag(vals[`${p}bo_is_company`] ?? vals.isCompany));
    slots.push({
      docCode: spec.docCode,
      contactCdKey,
      contactCd,
      name,
      isCompany,
      isSeniorManagement: spec.isSeniorManagement,
      smReasonId: spec.isSeniorManagement ? parseSmReasonId(vals, p) : 0,
      becomingDate,
      idNo,
      nationality,
      dob,
      gender,
      race,
      email,
      phone,
      occupation,
      residentialAddress,
      businessAddress,
      criteria: buildCriteria(criteriaByDoc, spec.docCode, vals, p),
      percents,
      ownPercent: resolveOwnPercent(percents),
      isJointArrangement: parseFlag(vals.is_joint_arrangement ?? vals.isJointArrangement),
    });
  }
  return slots;
}

/** CONTACT_CD 기준 중복 제거 — 먼저 등장한 슬롯 유지 (대소문자 무시) */
export function dedupeBoSlotsByContactCd(slots: BoAppendixRegisterSlot[]): BoAppendixRegisterSlot[] {
  const seen = new Set<string>();
  const out: BoAppendixRegisterSlot[] = [];
  for (const s of slots) {
    const cd = String(s.contactCd || '').trim().toUpperCase();
    if (cd) {
      if (seen.has(cd)) continue;
      seen.add(cd);
    }
    out.push(s);
  }
  return out;
}

const BO_DATE_KEYS = [
  'bo_date_became',
  'ba02_bo_date_became',
  'ba03_bo_date_became',
  'sm_appointment_date',
  'ba04_sm_appointment_date',
  'ba05_sm_appointment_date',
] as const;

/** 문서 DATA_JSON / values 에서 BO becoming / SM appointment 날짜 추출 */
export function digBoBecomingDateFromDataJson(obj: any, depth = 0): string {
  if (!obj || typeof obj !== 'object' || depth > 4) return '';
  for (const k of BO_DATE_KEYS) {
    const v = extractValue(obj[k]);
    if (v) return v;
  }
  for (const k of Object.keys(obj)) {
    if (/date_became$|appointment_date$/i.test(k)) {
      const v = extractValue(obj[k]);
      if (v) return v;
    }
  }
  for (const pool of [obj.values, obj.state?.values, obj.variables, obj.data, obj.formData]) {
    if (pool && typeof pool === 'object') {
      const found = digBoBecomingDateFromDataJson(pool, depth + 1);
      if (found) return found;
    }
  }
  return '';
}

function docMatchesBoSlot(docLabel: string, docCode: string): boolean {
  const label = String(docLabel || '').toUpperCase();
  const code = String(docCode || '').toUpperCase();
  if (!code) return false;
  if (label.includes(code)) return true;
  if (code === 'BA01' && /APPENDIX\s*A\b/.test(label)) return true;
  if ((code === 'BA02' || code === 'BA03') && /APPENDIX\s*B\b/.test(label)) return true;
  if ((code === 'BA04' || code === 'BA05') && /APPENDIX\s*C\b/.test(label)) return true;
  return false;
}

/**
 * Edit 로 문서에만 BO date 를 넣은 경우 TASK_DATA.presetForm 에는 비어 있을 수 있음.
 * 문서 DATA_JSON / fallbackDate 로 슬롯 becomingDate 를 보강한다.
 */
export function enrichBoSlotsBecomingDates(
  slots: BoAppendixRegisterSlot[],
  opts?: {
    fallbackDate?: string;
    docs?: Array<{ label?: string; dataJson?: any; DATA_JSON?: any; data_json?: any }>;
  },
): BoAppendixRegisterSlot[] {
  const fallback = String(opts?.fallbackDate || '').trim();
  const docs = Array.isArray(opts?.docs) ? opts!.docs! : [];

  const parseDocJson = (doc: any): any => {
    const raw = doc?.dataJson || doc?.DATA_JSON || doc?.data_json;
    if (!raw) return null;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return null; }
    }
    return typeof raw === 'object' ? raw : null;
  };

  return slots.map((slot) => {
    if (String(slot.becomingDate || '').trim()) return slot;
    let fromDoc = '';
    for (const doc of docs) {
      const label = String(doc?.label || (doc as any)?.docNm || (doc as any)?.DOC_NM || '');
      if (!docMatchesBoSlot(label, slot.docCode)) continue;
      fromDoc = digBoBecomingDateFromDataJson(parseDocJson(doc));
      if (fromDoc) break;
    }
    if (!fromDoc) {
      for (const doc of docs) {
        fromDoc = digBoBecomingDateFromDataJson(parseDocJson(doc));
        if (fromDoc) break;
      }
    }
    const next = fromDoc || fallback;
    if (!next) return slot;
    return { ...slot, becomingDate: next };
  });
}

export function splitBoSlotsByExistingContactCds(
  slots: BoAppendixRegisterSlot[],
  existingContactCds: Set<string> | string[],
): { toRegister: BoAppendixRegisterSlot[]; alreadyRegistered: BoAppendixRegisterSlot[] } {
  const set = new Set(
    (existingContactCds instanceof Set
      ? Array.from(existingContactCds)
      : Array.from(existingContactCds)
    )
      .map((c) => String(c || '').trim().toUpperCase())
      .filter(Boolean),
  );
  const toRegister: BoAppendixRegisterSlot[] = [];
  const alreadyRegistered: BoAppendixRegisterSlot[] = [];
  for (const s of dedupeBoSlotsByContactCd(slots)) {
    const cd = String(s.contactCd || '').trim().toUpperCase();
    if (cd && set.has(cd)) alreadyRegistered.push(s);
    else toRegister.push(s);
  }
  return { toRegister, alreadyRegistered };
}

/**
 * Legal Owner(IsBeneficialOwner=0 in BO_JSON_DATA) 는 실제 BO 가 아님 → 중복 체크 제외.
 * 플래그 누락 시에는 실제 BO 로 간주.
 */
export function isActualBeneficialOwnerRow(row: any): boolean {
  if (!row || typeof row !== 'object') return true;
  let flag: unknown = row.isBeneficialOwner ?? row.IsBeneficialOwner;
  if (flag === undefined || flag === null || String(flag).trim() === '') {
    const raw = row.boJsonData ?? row.BO_JSON_DATA ?? row.bo_json_data;
    let o: any = null;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) o = raw;
    else if (typeof raw === 'string' && raw.trim()) {
      try { o = JSON.parse(raw); } catch { o = null; }
    }
    if (o && typeof o === 'object') {
      flag = o.IsBeneficialOwner ?? o.isBeneficialOwner;
    }
  }
  if (flag === undefined || flag === null || String(flag).trim() === '') return true;
  return !(flag === 0 || flag === '0' || flag === false);
}
